import {
	Component,
	inject,
	OnDestroy,
	ChangeDetectionStrategy,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
	MAT_DIALOG_DATA,
	MatDialogRef,
	MatDialogTitle,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { DocusignService } from '@lmc-app/core/services/docusign.service';
import dayjs from 'dayjs';
import { catchError, finalize, of, Subject, takeUntil } from 'rxjs';
import {
	getLoanDetailsFields,
	getLoanNumberFields,
} from './form-interfaces/loan-amendment-form-interface';
import {
	LoanAmendmentDialogResult,
	LoanAmendmentFormModel,
} from '../interface/docusign-loan-amendment-interface';
import { DocusignEnvelopeRecipients } from '../interface/docusign-envelope-recipients-interface';
import { createDeliveryChannel } from '../helper-functions/docusign-utils';
import { User } from '@lmc-app/shared/interface/user.interface';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NgIf } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { AgreementsRecipientsComponent } from '../agreements/agreements-recipients/agreements-recipients.component';

@Component({
	selector: 'app-loan-amendment-dialog',
	templateUrl: './loan-amendment-dialog.component.html',
	styleUrls: ['./loan-amendment-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [
		// Each step slides in from the side it's coming from ({{offset}} is 0 on first open, so it only fades)
		trigger('stepSlide', [
			transition(
				':enter',
				[
					style({ opacity: 0, transform: 'translateX({{offset}})' }),
					animate('200ms ease-out', style({ opacity: 1, transform: 'none' })),
				],
				{ params: { offset: '0px' } }
			),
		]),
	],
	imports: [
		MatDialogTitle,
		CdkDrag,
		CdkDragHandle,
		MatIconButton,
		MatIcon,
		NgIf,
		ReactiveFormsModule,
		CdkScrollable,
		MatDialogContent,
		FormlyModule,
		MatDialogActions,
		MatButton,
		AgreementsRecipientsComponent,
	],
})
export class LoanAmendmentDialogComponent implements OnDestroy {
	readonly dialogRef = inject(MatDialogRef<LoanAmendmentDialogComponent>);
	readonly data = inject<LoanAmendmentDialogData>(MAT_DIALOG_DATA);
	private readonly _docusignService = inject(DocusignService);
	private readonly _destroy$ = new Subject<void>();

	step: LoanAmendmentStep = 'loan';
	slideOffset = '0px';

	loanForm = new FormGroup({});
	detailsForm = new FormGroup({});
	formlyModel: Partial<LoanAmendmentFormModel> = {};
	loanFields: FormlyFieldConfig[] = getLoanNumberFields('col-12 field-sm');
	detailsFields: FormlyFieldConfig[] = getLoanDetailsFields(
		new Date(),
		'col-12 field-sm'
	); // Executed Date can't be in the future

	loanSearching = false;
	loanSearchMessage = '';
	private _loadedLoanNumber = '';

	// Same recipient data model the Agreements tab uses (from normalizeAgreementData)
	recipientsData: any = null;
	recipientsLoading = false;
	recipientsMessage = '';

	ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
	}

	next(): void {
		if (this.loanSearching) {
			return;
		}
		if (this.loanForm.invalid) {
			this.loanForm.markAllAsTouched();
			return;
		}
		const loanNumber = String(this.formlyModel.NLSLoanNumber ?? '').trim();
		// Same loan as before (user went Back): keep any edits made to the details
		if (loanNumber === this._loadedLoanNumber) {
			this.goToStep('details');
			return;
		}
		this.loanSearchMessage = '';
		this.loanSearching = true;
		this._docusignService
			.getExistingLoanAmendment(loanNumber)
			.pipe(
				catchError(() => of(null)),
				finalize(() => (this.loanSearching = false)),
				takeUntil(this._destroy$)
			)
			.subscribe((res) => {
				if (!res?.success) {
					this.loanSearchMessage = 'Unable to search for this loan.';
					return;
				}
				const loan = res.data?.foundLoan ? res.data.loanDetails[0] : null;
				if (!loan) {
					this.loanSearchMessage = 'Loan not found.';
					return;
				}
				this.formlyModel = {
					...this.formlyModel,
					NLSLoanNumber: loanNumber,
					NLSOpenDate: loan.NLSOpenDate
						? dayjs(loan.NLSOpenDate).toDate()
						: undefined,
					NLSPrincipalAmount: loan.NLSPrincipalAmount,
					NLSInterestRate: loan.NLSInterestRate,
				};
				this._loadedLoanNumber = loanNumber;
				this.goToStep('details');
			});
	}

	nextToRecipients(): void {
		if (this.detailsForm.invalid) {
			this.detailsForm.markAllAsTouched();
			return;
		}
		this.goToStep('recipients');
		// Already loaded (user went Back): keep any reordering / channel changes
		if (this.recipientsData || this.recipientsLoading) {
			return;
		}
		this.recipientsMessage = '';
		this.recipientsLoading = true;
		this._docusignService
			.normalizeAgreementData(this.data.application, this.data.currentUser)
			.pipe(
				catchError(() => of(null)),
				finalize(() => (this.recipientsLoading = false)),
				takeUntil(this._destroy$)
			)
			.subscribe((res) => {
				this.recipientsData = res?.[0]?.recipients?.length ? res[0] : null;
				if (!this.recipientsData) {
					this.recipientsMessage = 'Unable to load recipients.';
				}
			});
	}

	back(): void {
		this.goToStep(this.step === 'recipients' ? 'details' : 'loan');
	}

	private goToStep(step: LoanAmendmentStep): void {
		this.slideOffset =
			STEP_ORDER.indexOf(step) > STEP_ORDER.indexOf(this.step)
				? '32px'
				: '-32px';
		this.step = step;
		// Recipient cards need more room than the loan fields
		this.dialogRef.updateSize(step === 'recipients' ? '900px' : '600px');
	}

	submit(): void {
		if (!this.recipientsData) {
			return;
		}
		const result: LoanAmendmentDialogResult = {
			loanAmendment: {
				type: this.data.type,
				NLSLoanNumber: String(this.formlyModel.NLSLoanNumber ?? '').trim(),
				NLSOpenDate: dayjs(this.formlyModel.NLSOpenDate).format('YYYY-MM-DD'),
				NLSPrincipalAmount: Number(
					String(this.formlyModel.NLSPrincipalAmount ?? '').replace(/,/g, '')
				), // currency input can hold "1,234.00"
				NLSInterestRate: Number(this.formlyModel.NLSInterestRate),
				AgentName: this.data.currentUser?.full_name ?? '',
				isInterestAmendment: this.data.type === 'Interest Rate' ? 'Yes' : 'No',
				zohoData: this.agreementFields(), // normalizeAgreementData fields, for the template's application fields
			},
			envelopeRecipients: this.createEnvelopeRecipients(
				this.recipientsData.recipients
			),
		};
		this.dialogRef.close(result);
	}

	// Same mapping Agreements uses on create, in the order shown on screen
	private createEnvelopeRecipients(
		recipients: any[]
	): Partial<DocusignEnvelopeRecipients>[] {
		const dealName = this.recipientsData?.DealName?.value;
		return recipients.map((recipient, i) => {
			const selected = Object.keys(recipient.delivery_option).filter(
				(key) => recipient.delivery_option[key] === true
			);
			const emailKey = selected.find((key) => key.toLowerCase() === 'email');
			const textKeys = selected.filter((key) => key !== emailKey);
			// Email first so createDeliveryChannel reads SMS/WhatsApp as the secondary channel
			const channelType = {
				deliveryType: 'multi',
				deliveryOptions: [emailKey, ...textKeys].filter(Boolean).join('+'),
			};
			const name = recipient?.name?.value?.toUpperCase();
			// Template roles can't have both email and a top-level phoneNumber: email-only gets no phone,
			// Email+SMS/WhatsApp puts the phone in additionalNotifications, SMS-only gets no email
			return {
				name,
				...(emailKey
					? {
							email: recipient?.email?.value,
							emailNotification: {
								emailSubject: `Loan Amendment - ${dealName}`,
								emailBody: `Hi ${name},<br><br>Please review and sign, loan amendment of ${dealName} with LM Credit Inc.<br><br>Thank you`,
								supportedLanguage: 'en',
							},
						}
					: {}),
				roleName: recipient?.role,
				recipientId: (i + 1).toString(),
				routingOrder: (i + 1).toString(),
				...(textKeys.length
					? createDeliveryChannel(recipient?.mobile?.value, channelType)
					: {}),
			};
		});
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	// normalizeAgreementData result minus the recipients (sent separately as envelopeRecipients)
	private agreementFields(): Record<string, any> {
		const { recipients, ...fields } = this.recipientsData ?? {};
		return fields;
	}
}

type LoanAmendmentStep = 'loan' | 'details' | 'recipients';
const STEP_ORDER: LoanAmendmentStep[] = ['loan', 'details', 'recipients'];

export interface LoanAmendmentDialogData {
	type: string;
	application: any;
	currentUser: User | null;
}
